import { Icon } from "@elit-hair/elit-ui";
import { useTranslation } from "react-i18next";
import { TbCheck, TbMicrophone, TbSpeakerphone } from "react-icons/tb";

import { cn } from "@/lib/utils";

export type AudioDeviceKind = "audioinput" | "audiooutput";

type AudioDeviceSelectorProps = {
    kind: AudioDeviceKind;
    devices: MediaDeviceInfo[];
    selectedDeviceId: string | null;
    onSelect: (deviceId: string) => void;
    /** Optional label override; defaults to t("output") or t("input") */
    label?: string;
    /** Optional empty state message; defaults to t("no_output_devices") or t("no_input_devices") */
    emptyMessage?: string;
    /** Optional content to render after the device label (e.g. test button for output in settings) */
    renderDeviceExtra?: (device: MediaDeviceInfo) => React.ReactNode;
    /** Optional class name for the container */
    className?: string;
    /** When true, the label row (icon + title) is not rendered */
    hideLabel?: boolean;
};

/**
 * Reusable selector for a single audio device type (input or output).
 * Used in the call-dock "device required" dialog and in settings audio device management.
 */
export function AudioDeviceSelector({
    kind,
    devices,
    selectedDeviceId,
    onSelect,
    label,
    emptyMessage,
    renderDeviceExtra,
    className,
    hideLabel = false,
}: AudioDeviceSelectorProps) {
    const { t } = useTranslation();

    const filtered = devices.filter((d) => d.kind === kind);
    const displayLabel = label ?? (kind === "audiooutput" ? t("output") : t("input"));
    const displayEmpty = emptyMessage ?? (kind === "audiooutput" ? t("no_output_devices") : t("no_input_devices"));
    const IconComponent = kind === "audiooutput" ? TbSpeakerphone : TbMicrophone;

    return (
        <div className={cn("space-y-2", className)}>
            {!hideLabel && (
                <div className="flex items-center gap-2 text-sm font-medium">
                    <Icon icon={IconComponent} className="text-muted-foreground" />
                    {displayLabel}
                </div>
            )}
            <div className="flex flex-col gap-1 max-h-40 overflow-y-auto rounded-lg border border-border/50 p-1">
                {filtered.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">{displayEmpty}</div>
                ) : (
                    filtered.map((device) => (
                        <button
                            key={device.deviceId}
                            type="button"
                            onClick={() => onSelect(device.deviceId)}
                            className={cn(
                                "w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors text-left",
                                selectedDeviceId === device.deviceId
                                    ? "bg-primary/15 text-primary"
                                    : "hover:bg-muted/50"
                            )}
                        >
                            <Icon icon={IconComponent} className="h-4 w-4 shrink-0" />
                            <span className="truncate flex-1 min-w-0">
                                {device.label || (kind === "audioinput" ? t("input") : displayLabel)}
                            </span>
                            {selectedDeviceId === device.deviceId && (
                                <Icon icon={TbCheck} className="shrink-0 text-primary" />
                            )}
                            {renderDeviceExtra?.(device)}
                        </button>
                    ))
                )}
            </div>
        </div>
    );
}
